import { useCallback, useRef } from "react"
import { useRemoteParticipants } from "@livekit/components-react"

import { roleOfIdentity } from "../envelope"
import { useRoomSync } from "../RoomSyncProvider"
import type { StageSurface } from "../stage/SyncStageOverlay"

/**
 * PointerLayer - the live pointer's capture and render surface (D-06, RS-09).
 *
 * Local capture converts a pointer event to a normalized source point via
 * `surface.transform.toSource` and publishes it through `useRoomSync().publishPointer`; remote
 * pointers are read back with `pointersFor` and placed with `surface.transform.toViewport`, so
 * both sides agree on where the dot sits regardless of screen size, orientation or mirroring.
 *
 * Never persisted (D-06): this file imports no API client, no transpiled handle and makes no
 * direct HTTP call of any kind - the data channel publish above is the only place a pointer
 * position ever goes.
 */
export function PointerLayer(p: { surface: StageSurface; active: boolean }) {
  const sync = useRoomSync()
  const remoteParticipants = useRemoteParticipants()
  const wasInsideRef = useRef(false)

  const nameFor = useCallback(
    (identity: string): string => {
      const participant = remoteParticipants.find((r) => r.identity === identity)
      if (participant?.name) {
        return participant.name
      }
      return roleOfIdentity(identity) === "worker" ? "Expert" : "Technician"
    },
    [remoteParticipants],
  )

  const publishAt = useCallback(
    (clientX: number, clientY: number, rect: DOMRect) => {
      const norm = p.surface.transform.toSource({ x: clientX - rect.left, y: clientY - rect.top })
      if (p.surface.transform.isInside(norm)) {
        wasInsideRef.current = true
        sync.publishPointer({
          src: p.surface.source,
          x: Math.min(1, Math.max(0, norm.x)),
          y: Math.min(1, Math.max(0, norm.y)),
        })
        return
      }
      if (wasInsideRef.current) {
        wasInsideRef.current = false
        sync.publishPointer(null)
      }
    },
    [p.surface, sync],
  )

  const hide = useCallback(() => {
    wasInsideRef.current = false
    sync.publishPointer(null)
  }, [sync])

  const others = sync
    .pointersFor(p.surface.source)
    .filter((pointer) => pointer.identity !== sync.localIdentity)

  return (
    <>
      {p.active && (
        <div
          className="cl:absolute cl:inset-0 cl:pointer-events-auto cl:touch-none"
          onPointerDown={(e) => publishAt(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect())}
          onPointerLeave={hide}
          onPointerMove={(e) => publishAt(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect())}
          onPointerUp={hide}
        />
      )}
      {others.map((pointer) => {
        const viewportPoint = p.surface.transform.toViewport({ x: pointer.payload.x, y: pointer.payload.y })
        return (
          <div
            key={pointer.identity}
            aria-hidden="true"
            className="cl:absolute cl:-translate-x-1/2 cl:-translate-y-1/2 cl:pointer-events-none cl:flex cl:flex-col cl:items-center"
            style={{ left: viewportPoint.x, top: viewportPoint.y, opacity: pointer.opacity }}
          >
            <span className="cl:relative cl:flex cl:size-4">
              <span className="cl:motion-safe:animate-ping cl:absolute cl:inline-flex cl:size-full cl:rounded-full cl:bg-primary/60" />
              <span className="cl:relative cl:inline-flex cl:size-4 cl:rounded-full cl:bg-primary" />
            </span>
            <span className="cl:mt-1 cl:whitespace-nowrap cl:rounded cl:bg-black/70 cl:px-1.5 cl:py-0.5 cl:text-[10px] cl:text-white">
              {nameFor(pointer.identity)}
            </span>
          </div>
        )
      })}
    </>
  )
}

export default PointerLayer
